import { Injectable } from '@angular/core';
import { UserClassesTaken } from '@app/shared/models/user-classes-taken';
import { BehaviorSubject, tap, Observable } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';
import { UserClassesService } from './user-classes.service';
import { AuthService } from './auth.service';
import { CourseProgressService } from './course-progress.service';
import { ConfigurationService } from './configuration.service';
import { UserEnrolmentService } from './user-enrolment.service';
import { UserCourseDto, createUserCourseDto } from '@models/user-course-dto';

@Injectable({
  providedIn: 'root'
})
export class UserClassesStateService {

  private userClassesSubject = new BehaviorSubject<UserClassesTaken[]>([]);
  public userClasses$ = this.userClassesSubject.asObservable();

  constructor(
    private userClassesService: UserClassesService,
    private authService: AuthService,
    private courseProgressService: CourseProgressService,  // Integration point
    private configurationService: ConfigurationService,
    private userEnrolmentService: UserEnrolmentService
  ) {}

  loadUserClasses() {
    return this.userClassesService.GetAllClassesTakenByUser(
      this.authService.returnCurrentUser().id
    ).pipe(
      tap((data: UserClassesTaken[]) => {
        // Normalize and ensure 10 classes exist
        const normalizedClasses = this.normalizeUserClasses(data);
        this.userClassesSubject.next(normalizedClasses);
        
        // Update high-level progress when detailed data changes
        const completedActivities = this.calculateCompletedActivities(normalizedClasses);
        this.courseProgressService.updateProgress(completedActivities);
      })
    );
  }

  private normalizeUserClasses(rawData: UserClassesTaken[]): UserClassesTaken[] {
    const userId = this.authService.returnCurrentUser().id;
    const normalizedClasses: UserClassesTaken[] = [];
    
    // Create Map for easy lookup
    const classMap = new Map<number, UserClassesTaken>();
    rawData.forEach(cls => classMap.set(cls.classId, cls));
    
    // Ensure exactly 10 classes (1-10) exist
    for (let i = 1; i <= 10; i++) {
      if (classMap.has(i)) {
        normalizedClasses.push(classMap.get(i)!);
      } else {
        // Create default class object for missing classes
        normalizedClasses.push({
          classId: i,
          userId: userId,
          videoLength: 0,
          videoWatched: 0,
          isQuizCompleted: false,
          isVideoCompleted: false,
          isHandoutCompleted: false,
          whenClassStarted: null,
          whenClassCompleted: null,
          isAvailable: false,
        });
      }
    }
    
    return normalizedClasses;
  }

  private calculateCompletedActivities(classes: UserClassesTaken[]): number {
    return classes.reduce((total, cls) => {
      let count = 0;
      if (cls.isVideoCompleted) count++;
      if (cls.isHandoutCompleted) count++;
      if (cls.isQuizCompleted) count++;
      return total + count;
    }, 0);
  }

  getUserClasses(): UserClassesTaken[] {
    return this.userClassesSubject.value;
  }

  getClassById(classId: number): UserClassesTaken | undefined {
    return this.userClassesSubject.value.find(cls => cls.classId === classId);
  }

  getClassesAsMap(): Map<number, UserClassesTaken> {
    const classMap = new Map<number, UserClassesTaken>();
    this.userClassesSubject.value.forEach(cls => classMap.set(cls.classId, cls));
    return classMap;
  }

  refreshUserClasses() {
    this.loadUserClasses().subscribe();
  }

  /**
   * Enhanced method using UserEnrolmentService for cached enrollment data
   * Checks course completion status and handles certificate generation trigger
   * Simplifies the logic that was previously scattered across components
   */
  checkAndUpdateCourseCompletion(): Observable<{ completed: number; isFullyComplete: boolean }> {
    return this.userEnrolmentService.getEnrollmentDataWithFallback().pipe(
      switchMap((enrollmentData: UserCourseDto) => {
        return this.userClassesService.CheckAllClassesComplete(enrollmentData).pipe(
          tap((courseCompleted: number) => {
            // Update percentage in localStorage (legacy requirement)
            const percentageWatched = courseCompleted * 100 / 30;
            localStorage.setItem('percentage', percentageWatched.toString());

            // Check if course is fully complete
            if (this.configurationService.isCourseComplete(courseCompleted)) {
              if (this.authService.getCourseCompleted() === "false") {
                // Mark course as completed in localStorage
                this.authService.setCourseCompleted();
                
                // Update course progress service to trigger reactive completion
                this.courseProgressService.updateProgress(courseCompleted);
              }
            }
          }),
          // Transform the response to include completion status
          map((courseCompleted: number) => ({
            completed: courseCompleted,
            isFullyComplete: this.configurationService.isCourseComplete(courseCompleted)
          }))
        );
      }),
      catchError(error => {
        console.warn('Error checking course completion, using fallback:', error);
        // Use fallback DTO creation if enrollment service fails
        const fallbackDto = createUserCourseDto({
          userId: this.authService.returnCurrentUser().id,
          courseId: this.authService.returnCourseId(),
          isCourseComplete: this.authService.getCourseCompleted()
        });
        
        return this.userClassesService.CheckAllClassesComplete(fallbackDto).pipe(
          map((courseCompleted: number) => ({
            completed: courseCompleted,
            isFullyComplete: this.configurationService.isCourseComplete(courseCompleted)
          }))
        );
      })
    );
  }

  /**
   * Convenience method to get course completion using enrollment service
   */
  getCourseCompletionWithEnrollment(): Observable<{ completed: number; isFullyComplete: boolean }> {
    return this.userEnrolmentService.getEnrollmentDataWithFallback().pipe(
      switchMap(enrollmentData => {
        return this.userClassesService.CheckAllClassesComplete(enrollmentData).pipe(
          map((courseCompleted: number) => ({
            completed: courseCompleted,
            isFullyComplete: this.configurationService.isCourseComplete(courseCompleted)
          }))
        );
      })
    );
  }
}
